<script setup>
import { DateTime } from "luxon";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import Tooltip from "./ui/Tooltip.vue";

const EXPIRING_COLOUR_CHANGE_TIME = 15 * 60; // Seconds

const { t } = useI18n();

const props = defineProps({
  status: {
    type: Number,
    rquired: true,
  },
  error: {
    type: String,
    rquired: true,
  },
  expiredAfter: {
    type: String,
    required: false,
  },
  clickToGrant: {
    type: Boolean,
    required: true,
  },
});

const expiresAfter = ref(DateTime.fromISO(props.expiredAfter, { zone: "utc" }));
const expiresDiff = computed(() => (expiresAfter.value ? expiresAfter.value.diffNow("seconds").seconds : null));
const expiresRelative = computed(() => (expiresAfter.value ? expiresAfter.value.toRelative() : null));
const expiresSoon = computed(() => Boolean(expiresAfter.value) && expiresDiff.value < EXPIRING_COLOUR_CHANGE_TIME);

var interval;

onMounted(() => {
  interval = setInterval(() => {
    expiresAfter.value = DateTime.fromISO(props.expiredAfter, { zone: "utc" });
  }, 1000);
});

onUnmounted(() => {
  clearInterval(interval);
});
</script>

<template>
  <div>
    <Tooltip :focusable="true" aria-label="ACL status" :text="error">
      <div
        :class="
          twMerge(
            'inline-flex select-none items-center text-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            status === 0 && 'border-green-500/20 bg-green-500/10 text-green-500',
            status === 1 && 'border-destructive/20 bg-destructive/10 text-destructive',
            status === 2 && 'bg-warning/10 text-warning border-warning/20',
            status === 3 && 'border-destructive/20 bg-destructive/10 text-destructive',
            status === 4 && 'hidden',
            status === 5 && 'border-green-500/20 bg-green-500/10 text-green-500',
            status === 6 && 'border-green-500/20 bg-green-500/10 text-green-500',
            status === 8 && 'bg-warning/10 text-warning border-warning/20',
            status === 9 && 'text-foreground',
            status === 10 && 'border-destructive/20 bg-destructive/10 text-destructive',
            expiresSoon && 'border-orange-500/20 bg-orange-500/10 text-orange-500',
          )
        "
      >
        <template v-if="Boolean(expiresAfter) && expiresDiff > 0">
          {{ (clickToGrant ? "Access" : t(`statuses.${status}`)) + " for " + expiresRelative.replaceAll("in ", "") }}
        </template>
        <template v-else>
          {{ t(`statuses.${status}`) }}
        </template>
      </div>
    </Tooltip>
  </div>
</template>
